import React from 'react';
import PropTypes from 'prop-types';
import Criteo from './CriteoBidder';
import Id from './AdManagerId';
import SlotSizes from './AdManagerSlotSizes';

import styles from './AdManager.module.scss';

/* eslint-disable no-undef */

let isLoaded = false;
const reset = () => isLoaded = false;

const SRA_DELAY_MS = 1800;

const SlotClassNames = {
    [Id.Footer]: styles['footer-mobile-banner'],
    [Id.Sidebar]: styles['sidebar-banner'],
    [Id.Promo]: `${styles['responsive-banner']} ${styles['promo-banner']}`,
    [Id.Category1]: `${styles['responsive-banner']} ${styles['category-banner']}`,
    [Id.Category2]: `${styles['responsive-banner']} ${styles['category-banner']}`,
    [Id.Category3]: `${styles['responsive-banner']} ${styles['category-banner']}`,
    [Id.CategoryMid]: `${styles['responsive-banner']} ${styles['category-middle-banner']}`,
    [Id.ProductMid]: styles['product-middle-banner'],
    [Id.ProductMidMobile]: styles['product-middle-mobile-banner'],
    [Id.HomeSlider1]: `${styles['responsive-banner']} ${styles['home-slider']}`,
    [Id.HomeSlider2]: `${styles['responsive-banner']} ${styles['home-slider']}`,
    [Id.HomeSlider3]: `${styles['responsive-banner']} ${styles['home-slider']}`,
    [Id.HomeSlider4]: `${styles['responsive-banner']} ${styles['home-slider']}`,
    [Id.HomeMid1]: `${styles['responsive-banner']} ${styles['home-middle-1x']}`,
    [Id.HomeMid21]: `${styles['responsive-banner']} ${styles['home-middle-2x']}`,
    [Id.HomeMid22]: `${styles['responsive-banner']} ${styles['home-middle-2x']}`,
    [Id.HomeMid31]: `${styles['responsive-banner']} ${styles['home-middle-3x']}`,
    [Id.HomeMid32]: `${styles['responsive-banner']} ${styles['home-middle-3x']}`,
    [Id.HomeMid33]: `${styles['responsive-banner']} ${styles['home-middle-3x']}`,
    [Id.CategoryLandingTopSlider1]: `${styles.banner} ${styles['category-top-slider']}`,
    [Id.CategoryLandingTopSlider2]: `${styles.banner} ${styles['category-top-slider']}`,
    [Id.CategoryLandingTopSlider3]: `${styles.banner} ${styles['category-top-slider']}`,
    [Id.CategoryLandingTopSlider4]: `${styles.banner} ${styles['category-top-slider']}`,
    [Id.CategoryLandingFeatured]: `${styles.banner} ${styles['category-featured']}`,
    [Id.CategoryLandingFeaturedWeek1]: `${styles.banner} ${styles['category-featured-week']}`,
    [Id.CategoryLandingFeaturedWeek2]: `${styles.banner} ${styles['category-featured-week']}`,
    [Id.CategoryLandingFeaturedWeek3]: `${styles.banner} ${styles['category-featured-week']}`,
    [Id.CategoryLandingMiddle1]: `${styles.banner} ${styles['category-middle']}`,
    [Id.CategoryLandingMiddle2]: `${styles.banner} ${styles['category-middle']}`,
    [Id.CategoryLandingMiddle3]: `${styles.banner} ${styles['category-middle']}`,
    [Id.CategoryLandingMiddle4]: `${styles.banner} ${styles['category-middle']}`,
    [Id.CategoryLandingBrand1]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand2]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand3]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand4]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand5]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand6]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand7]: `${styles.banner} ${styles['category-brand']}`,
    [Id.CategoryLandingBrand8]: `${styles.banner} ${styles['category-brand']}`,
};

const initScript = () => {
    window.googletag = window.googletag || {};
    window.googletag.cmd = window.googletag.cmd || [];

    const externalScript = '//securepubads.g.doubleclick.net/tag/js/gpt.js';
    const script = document.createElement('script');
    script.src = externalScript;
    script.async = true;
    script.setAttribute('fetchpriority', 'high');
    document.head.appendChild(script);
};

// TODO @deprecated use camelCase properties -- remove once all banners migrated to Nextjs
// istanbul ignore next
const mapProps = (ads) => ads.map((m) => ({
    ...m,
    divId: m.divId || m.div_id,
    adUnitPath: m.adUnitPath || m.ad_unit_path,
}));
// end -- TODO

const loadSlots = (ads, lang, options = {}) => {
    window.googletag.cmd.push(() => {

        // Define primary slots
        const slots = ads.filter((ad) => ad.isPriorityBanner).map((ad) => defineSlot({ ...ad, lang }));

        // Disable initial load to precisely control when ads are requested.
        window.googletag.pubads().disableInitialLoad();

        // Enable lazy load
        window.googletag.pubads().enableLazyLoad({
            renderMarginPercent: 10
        });

        // Enable SRA and services
        if (!options.disableSingleRequest) {
            window.googletag.pubads().enableSingleRequest();
        }

        // Show blank space by default and collapse if it cannot be filled
        window.googletag.pubads().collapseEmptyDivs(false);

        // Fetch slots via Criteo
        // Fetch high priority slots
        // istanbul ignore next
        Criteo.fetch(slots.map(({ slot }) => slot));

        window.googletag.enableServices();

        // Display ads
        slots.forEach(({ ad }) => window.googletag.display(ad.divId));

        // istanbul ignore next
        slots.forEach(({ slot }) => {
            window.matchMedia('screen and (min-width: 768px)').addEventListener('change', () => refreshAd(slot));
            window.matchMedia('screen and (min-width: 1035px)').addEventListener('change', () => refreshAd(slot));
            window.matchMedia('screen and (min-width: 1415px)').addEventListener('change', () => refreshAd(slot));
        });
    });

    // istanbul ignore next
    setTimeout(() => {
        window.googletag.cmd.push(() => {
            // Define and load secondary slots
            const slots = ads.filter((ad) => !ad.isPriorityBanner).map((ad) => {
                const slot = defineSlot({ ...ad, lang });
                window.googletag.display(slot.ad.divId);
                return slot;
            });
            Criteo.lazy(slots.map(({ slot }) => slot));

            slots.forEach(({ slot }) => {
                window.matchMedia('screen and (min-width: 768px)').addEventListener('change', () => refreshAd(slot));
                window.matchMedia('screen and (min-width: 1035px)').addEventListener('change', () => refreshAd(slot));
                window.matchMedia('screen and (min-width: 1415px)').addEventListener('change', () => refreshAd(slot));
            });
        });
    }, SRA_DELAY_MS);
};

const defineSlot = (ad) => {
    const sizeMapping = window.googletag.sizeMapping();
    (SlotSizes[ad.id] || []).map(size => sizeMapping.addSize(...size));
    const sizeMappingBuild = sizeMapping.build();
    const slot = window.googletag.defineSlot(ad.adUnitPath, [0, 0], ad.divId);
    slot.setTargeting('domain', [ad.lang === 'es' ? 'default' : ad.lang]);
    Object.keys(ad.targeting || {}).forEach(key => slot.setTargeting(key, ad.targeting[key]));
    slot.defineSizeMapping(sizeMappingBuild);
    slot.addService(window.googletag.pubads());
    return { ad, slot };
};

// istanbul ignore next
const refreshAd = (slot) => (window.googletag || {}).pubads && window.googletag.pubads().refresh([slot]);

const init = (ads, lang, options) => {
    if (isLoaded) return;
    isLoaded = true;
    initScript();
    Criteo.init();
    loadSlots(mapProps(ads), lang, options);
};

const AdManager = ({ id, divId, onClick }) => (
    <div
        id={divId}
        className={`${styles['ad-manager-component']} ${SlotClassNames[id] || ''}`}
        onClick={onClick || null}
    />
);

AdManager.propTypes = {
    id: PropTypes.string,
    divId: PropTypes.string,
    onClick: PropTypes.func,
};

export {
    init,
    Id as AdManagerId,
    AdManager as render,
    reset,
};

export default AdManager;